import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router";
import { useWatch } from "react-hook-form";
import { useArtworkForm } from "@design-lab/entities/artwork";
import { trackDesignLabSessionStart } from "@design-lab/shared/lib/analytics";

// The enhancements_started/dtf_channel_uuid handoff resolves dtf_artwork_id
// asynchronously; if it never resolves we still must record the session.
const ARTWORK_RESOLVE_FALLBACK_MS = 8000;

// The custom-tee / Made / Transfer PDP→DL handoff redirects with `artwork_urls`,
// whose JSON key IS the dtf_artwork_id (dtf_artworks_controller.rb:1169
// `artwork_urls[artwork.id] = ...`), so the id is known synchronously at mount.
// Guard on a numeric key: dtf_artwork ids are numeric DB ids, whereas the staff
// share-link flow writes `upload_urls` side-keyed ({ front, back } —
// ShareDesignLink.tsx:37-44). Parsing only numeric `artwork_urls` keys avoids
// emitting "front"/"back" as an artwork_id. Returns the last key (matches
// useEnhancementsStarted.ts:47).
function parseArtworkIdFromArtworkUrls(searchParams: URLSearchParams): string | undefined {
  const raw = searchParams.get("artwork_urls");
  if (!raw) return undefined;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === "object") {
      const keys = Object.keys(parsed as Record<string, unknown>);
      const id = keys[keys.length - 1];
      return id && /^\d+$/.test(id) ? id : undefined;
    }
  } catch {
    // Malformed param — treat as no id; the deferred path still records the session.
  }
  return undefined;
}

// Pick the artwork id for the side the handoff targets; fall back to either side when the
// handoff names no side. Avoids reporting a stale opposite-side id from a restored project.
function resolveTargetArtworkId(
  targetSide: string | null,
  frontSideArtworkId: string | undefined,
  backArtworkId: string | undefined
): string | undefined {
  if (targetSide === "back") return backArtworkId;
  if (targetSide === "front") return frontSideArtworkId;
  return frontSideArtworkId || backArtworkId;
}

export function useTrackDesignLabSessionStart() {
  const hasFired = useRef(false);
  const [searchParams] = useSearchParams();

  // Snapshot the origin ONCE at mount. The DL strips its handoff params from the URL
  // after the artwork API resolves (useEnhancementsStarted.ts:135-144 /
  // design-lab-init), so reading them reactively would flip the branch mid-session and
  // mis-fire. useRef initializers run exactly once.

  // (a) artwork_urls handoff with a numeric key: the id is in the URL, so fire
  // immediately with it — no deferral.
  const urlArtworkIdRef = useRef(parseArtworkIdFromArtworkUrls(searchParams));

  // (b) Deferred handoffs where the id is NOT readable from the URL and resolves into the
  // form asynchronously: the enhancements_started + dtf_channel_uuid path, plus any
  // artwork_urls/upload_urls external upload (e.g. side-keyed share links) that did not
  // yield a numeric id above. An empty `?dtf_channel_uuid=` is not a real handoff
  // (useEnhancementsStarted.ts:56 requires a truthy uuid), so use a truthy check.
  const isDeferredPdpRef = useRef(
    (searchParams.get("enhancements_started") === "true" &&
      !!searchParams.get("dtf_channel_uuid")) ||
      searchParams.get("artwork_urls") != null ||
      searchParams.get("upload_urls") != null
  );

  // The handoff names the side it targets (the controller forwards `side`). Prefer that
  // side's artwork so a one-side upload into a project that still has existing
  // opposite-side artwork (restored by useFormRestoration) does not report the stale
  // opposite-side id. Snapshot once at mount.
  const targetSideRef = useRef(searchParams.get("side"));

  const { form } = useArtworkForm();
  const frontArtworkId = useWatch({ control: form.control, name: "front.artworkId" });
  const backArtworkId = useWatch({ control: form.control, name: "back.artworkId" });
  // front_left_chest is the resolved placement for products whose only front side is
  // front_left_chest (useArtworkForm.ts:83-89) — the artwork lands there, not on `front`.
  const frontLeftChestArtworkId = useWatch({
    control: form.control,
    name: "frontLeftChest.artworkId"
  });
  const frontSideArtworkId = frontArtworkId || frontLeftChestArtworkId || undefined;
  const artworkId = resolveTargetArtworkId(
    targetSideRef.current,
    frontSideArtworkId,
    backArtworkId || undefined
  );

  // Keep the latest resolved id readable from the fallback timeout: if the id lands
  // just before the 8s boundary, the fallback still fires with it rather than undefined.
  const artworkIdRef = useRef<string | undefined>(undefined);
  artworkIdRef.current = artworkId;

  const fire = (id?: string) => {
    if (!hasFired.current) {
      hasFired.current = true;
      trackDesignLabSessionStart(id);
    }
  };

  // Mount: external-upload handoff fires immediately with the URL-parsed id; non-PDP
  // sessions fire immediately with none. The deferred handoff waits (effects below).
  useEffect(() => {
    if (urlArtworkIdRef.current) fire(urlArtworkIdRef.current);
    else if (!isDeferredPdpRef.current) fire();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deferred handoff: fire once the artwork id resolves into the form.
  useEffect(() => {
    if (isDeferredPdpRef.current && artworkId) fire(artworkId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artworkId]);

  // Deferred-handoff fallback: never drop the event if the id never resolves; still emit
  // the latest known id if one landed right at the boundary. On unmount, flush the event
  // (with whatever id has resolved) so a navigate-away inside the window still records the
  // session — matching the old route-level effect that fired the open immediately. `fire`
  // is idempotent, so an already-fired session is a no-op.
  useEffect(() => {
    if (!isDeferredPdpRef.current) return undefined;
    const t = setTimeout(() => fire(artworkIdRef.current), ARTWORK_RESOLVE_FALLBACK_MS);
    return () => {
      clearTimeout(t);
      fire(artworkIdRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
